/**
 * use-platform.ts — TanStack Query hooks cho khu vực Platform (super-admin).
 * Tất cả gọi qua `platformApi` (token platform riêng), KHÔNG dùng `api` của CRM.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from '@/lib/platform-client'
import { clearRefreshToken, setTokens } from '@/lib/api-client'

// ── Kiểu dữ liệu ──────────────────────────────────────────────────
export type OrgDisplayStatus = 'active' | 'suspended' | 'expired' | 'unlimited'

export interface OrgStats {
  users: number
  contacts: number
  conversations: number
}

export interface OrgListItem {
  id: string
  name: string
  status: string
  expiresAt: string | null
  plan: string | null
  adminNotes: string | null
  createdAt: string
  updatedAt: string
  displayStatus: OrgDisplayStatus
  expiringSoon: boolean
  stats: OrgStats
  /** Đã xoá mềm (chỉ có khi xem danh sách "Đã xoá"). */
  deletedAt?: string | null
  /** Tự xoá vĩnh viễn lúc này (null = không tự xoá). */
  purgeAt?: string | null
}

export interface OrgUser {
  id: string
  email: string
  fullName: string
  role: 'owner' | 'admin' | 'manager' | 'member'
  isActive: boolean
  createdAt: string
}

export interface OrgDetail extends OrgListItem {
  users: OrgUser[]
}

export interface OrgListResponse {
  items: OrgListItem[]
  meta: { page: number; pageSize: number; total: number; totalPages: number; deletedCount?: number }
}

export interface ReportOverview {
  companies: {
    total: number
    active: number
    suspended: number
    expired: number
    unlimited: number
    expiringIn7d: number
    newLast30d: number
  }
  users: number
  contacts: number
  conversations: number
}

// ── Reports ───────────────────────────────────────────────────────
export function usePlatformOverview() {
  return useQuery<ReportOverview>({
    queryKey: ['platform', 'overview'],
    queryFn: async () => {
      const { data } = await platformApi.get('/platform/reports/overview')
      return data
    },
  })
}

// ── Danh sách tổ chức ─────────────────────────────────────────────
export function usePlatformOrgs(params: { search?: string; page?: number; pageSize?: number; deleted?: '1' }) {
  return useQuery<OrgListResponse>({
    queryKey: ['platform', 'orgs', params],
    queryFn: async () => {
      const { data } = await platformApi.get('/platform/orgs', { params })
      return data
    },
    placeholderData: (prev) => prev,
  })
}

export function usePlatformOrg(orgId: string | undefined) {
  return useQuery<OrgDetail>({
    queryKey: ['platform', 'org', orgId],
    queryFn: async () => {
      const { data } = await platformApi.get(`/platform/orgs/${orgId}`)
      return data
    },
    enabled: !!orgId,
  })
}

// ── Tạo tổ chức + chủ sở hữu ──────────────────────────────────────
export interface CreateOrgInput {
  orgName: string
  ownerFullName: string
  ownerEmail: string
  ownerPassword: string
  expiresAt?: string | null
  plan?: string | null
  /** Tiếp thị liên kết: mã người giới thiệu + lead nguồn (tuỳ chọn). */
  referralCode?: string | null
  leadId?: string | null
}

export function useCreateOrg() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateOrgInput) => {
      const { data } = await platformApi.post('/platform/orgs', input)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'orgs'] }),
  })
}

// ── Cập nhật thông tin tổ chức (name / plan / notes) ──────────────
export function useUpdateOrg(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    // C9: endpoint cũ bắt buộc lý do (≥ 3 ký tự) như endpoint mới. Q4: sang gói trả phí phải kèm expiresAt (ISO, tương lai).
    mutationFn: async (data: { name?: string; plan?: string | null; adminNotes?: string | null; reason: string; expiresAt?: string }) => {
      const res = await platformApi.patch(`/platform/orgs/${orgId}`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'org', orgId] })
      qc.invalidateQueries({ queryKey: ['platform', 'orgs'] })
    },
  })
}

// ── Cấp phép: gia hạn (expiresAt) / khóa (status) ─────────────────
export function useUpdateOrgLicense(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { expiresAt?: string | null; status?: 'active' | 'suspended'; reason: string }) => {
      const res = await platformApi.patch(`/platform/orgs/${orgId}/license`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'org', orgId] })
      qc.invalidateQueries({ queryKey: ['platform', 'orgs'] })
      qc.invalidateQueries({ queryKey: ['platform', 'overview'] })
    },
  })
}

// ── Quản lý user của tổ chức ──────────────────────────────────────
export function useAddOrgUser(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { fullName: string; email: string; password: string; role?: string }) => {
      const res = await platformApi.post(`/platform/orgs/${orgId}/users`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'org', orgId] }),
  })
}

export function useSetOrgUserActive(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { userId: string; isActive: boolean; reason: string }) => {
      const res = await platformApi.patch(`/platform/orgs/${orgId}/users/${vars.userId}`, {
        isActive: vars.isActive, reason: vars.reason,
      })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'org', orgId] }),
  })
}

export function useResetOrgUserPassword(orgId: string) {
  return useMutation({
    mutationFn: async (vars: { userId: string; newPassword: string; reason: string }) => {
      const res = await platformApi.post(
        `/platform/orgs/${orgId}/users/${vars.userId}/reset-password`,
        { newPassword: vars.newPassword, reason: vars.reason },
      )
      return res.data
    },
  })
}

// ── "Vào công ty": login-as → nhận company token → mở CRM ─────────
export function useEnterCompany() {
  return useMutation({
    mutationFn: async (vars: { orgId: string; userId?: string; reason: string }) => {
      const { data } = await platformApi.post(`/platform/orgs/${vars.orgId}/login-as`, {
        userId: vars.userId, reason: vars.reason,
      })
      return data as { token: string; user: unknown }
    },
    onSuccess: (data) => {
      // Ghi token company vào key của CRM rồi điều hướng full-reload sang CRM.
      // login-as chỉ cấp access token (4h, không refresh) nên xóa refresh cũ.
      // Qua kho token chung: xoá cả bản sessionStorage (phiên "không duy trì") để token cũ không che token mới.
      setTokens(data.token, undefined, true)
      clearRefreshToken()
    },
  })
}

// ── Branding toàn hệ thống ────────────────────────────────────────
export interface BrandingMeta {
  brandName: string | null
  tagline?: string | null
  logoUrl?: string | null
  primaryColor?: string | null
  logoVersion: string | null
  faviconVersion: string | null
}

export function usePlatformBranding() {
  return useQuery<BrandingMeta>({
    queryKey: ['platform', 'branding'],
    queryFn: async () => {
      const { data } = await platformApi.get('/platform/branding')
      return data
    },
  })
}

export function useUpdateBrandName() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { brandName: string; tagline?: string; primaryColor?: string }) => {
      const res = await platformApi.put('/platform/branding', body)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'branding'] }),
  })
}

export function useUploadBrandingImage(kind: 'logo' | 'favicon') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (dataUrl: string) => {
      const res = await platformApi.put(`/platform/branding/${kind}`, { dataUrl })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'branding'] }),
  })
}

export function useClearBrandingImage(kind: 'logo' | 'favicon') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await platformApi.delete(`/platform/branding/${kind}`)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'branding'] }),
  })
}

// ── Nhật ký hành động super-admin (audit log) ──────────────────────
export interface PlatformAuditItem {
  id: string
  action: string
  createdAt: string
  admin: { id: string; fullName: string; email: string } | null
  targetOrgId: string | null
  targetOrgName: string | null
  targetUserId: string | null
  targetUserName: string | null
  meta: Record<string, unknown>
}

export interface PlatformAuditResponse {
  items: PlatformAuditItem[]
  total: number
  page: number
  limit: number
}

export function usePlatformAudit(params: { action?: string; orgId?: string; page?: number; limit?: number }) {
  return useQuery<PlatformAuditResponse>({
    queryKey: ['platform', 'audit', params],
    queryFn: async () => {
      const { data } = await platformApi.get('/platform/audit', { params })
      return data
    },
  })
}

export function usePlatformAuditActions() {
  return useQuery<string[]>({
    queryKey: ['platform', 'audit', 'actions'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await platformApi.get<{ actions: string[] }>('/platform/audit/actions')
      return data.actions
    },
  })
}

// ── Vận hành: sức khoẻ kênh ────────────────────────────────────────
export interface ChannelHealthItem {
  id: string
  orgId: string
  orgName: string
  platform: number
  displayName: string | null
  status: string
  isDisabled: boolean
  tokenExpiresAt: string | null
  lastConnectedAt: string | null
  tokenExpiring: boolean
  tokenExpired: boolean
}

export interface ChannelHealthResponse {
  items: ChannelHealthItem[]
  summary: {
    total: number; connected: number; disconnected: number; disabled: number
    tokenExpiring: number; tokenExpired: number; orgsWithIssue: number
  }
  tokenWarnDays: number
}

export function usePlatformChannelHealth() {
  return useQuery<ChannelHealthResponse>({
    queryKey: ['platform', 'channels', 'health'],
    queryFn: async () => (await platformApi.get('/platform/channels/health')).data,
  })
}

// ── Vận hành: usage & chi phí AI theo tenant ───────────────────────
export interface AiUsageItem {
  orgId: string
  orgName: string
  calls: number
  costUsd: number
  tokensIn: number
  tokensOut: number
  maxDaily: number | null
  aiEnabled: boolean | null
  /** Khung ba tầng — Platform khoá AI của tổ chức (server cũ: undefined). */
  platformAiDisabled?: boolean | null
  /** Khung ba tầng — trần lượt / ngày do Platform đặt (null = không đặt; server cũ: undefined). */
  platformMaxDaily?: number | null
}

export interface AiUsageResponse {
  days: number
  items: AiUsageItem[]
  totals: { calls: number; costUsd: number; tokensIn: number; tokensOut: number }
}

export function usePlatformAiUsage(days: number) {
  return useQuery<AiUsageResponse>({
    queryKey: ['platform', 'ai', 'usage', days],
    queryFn: async () => (await platformApi.get('/platform/ai/usage', { params: { days } })).data,
  })
}

/**
 * Khoá AI / trần lượt mỗi ngày do PLATFORM đặt cho một tổ chức (khung ba tầng): ghi `platformAiDisabled` /
 * `platformMaxDaily` — tổ chức không sửa được, không đè cài đặt riêng của tổ chức (`enabled` / `maxDaily`).
 */
export function useSetAiQuota() {
  const qc = useQueryClient()
  return useMutation<
    { orgId: string; platformAiDisabled?: boolean; platformMaxDaily?: number | null },
    unknown,
    { orgId: string; platformAiDisabled?: boolean; platformMaxDaily?: number | null }
  >({
    mutationFn: async ({ orgId, ...body }) =>
      (await platformApi.patch(`/platform/orgs/${orgId}/ai-quota`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'ai', 'usage'] }),
  })
}

// ── Quản lý super-admin ────────────────────────────────────────────
export interface PlatformAdminItem {
  id: string
  email: string
  fullName: string
  isActive: boolean
  lastLoginAt: string | null
  createdAt: string
  isSelf: boolean
}

export function usePlatformAdmins() {
  return useQuery<{ admins: PlatformAdminItem[]; activeCount: number }>({
    queryKey: ['platform', 'admins'],
    queryFn: async () => (await platformApi.get('/platform/admins')).data,
  })
}

export function useCreatePlatformAdmin() {
  const qc = useQueryClient()
  return useMutation<PlatformAdminItem, unknown, { email: string; fullName: string; password: string }>({
    mutationFn: async (body) => (await platformApi.post('/platform/admins', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'admins'] }),
  })
}

export function useSetPlatformAdminActive() {
  const qc = useQueryClient()
  return useMutation<PlatformAdminItem, unknown, { id: string; isActive: boolean }>({
    mutationFn: async ({ id, isActive }) => (await platformApi.patch(`/platform/admins/${id}`, { isActive })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'admins'] }),
  })
}

export function useLogoutAllPlatformAdmin() {
  return useMutation<{ success: boolean }, unknown, string>({
    mutationFn: async (id) => (await platformApi.post(`/platform/admins/${id}/logout-all`)).data,
  })
}
